/**
 * Only allow keys, whose values are of type `string`.
 */
type KeysOfStringValue<T> = {
    [K in keyof T]: T[K] extends string ? K : never;
}[keyof T];

export const getRandomArrayElement = <T>(items: T[]) =>
    items[Math.floor(Math.random() * items.length)];

/**
 * Accepts an array of records and a desired key, and returns a record
 * whose keys is the value of T[key], and the value is T.
 * @example
 *  items = [{ a: 'value1', b: 'value2' }, { a: 'value3', b: 'value4' }]
 *  key = 'a'
 *
 *  result = {
 *      'value1': { a: 'value1', b: 'value2' },
 *      'value3': { a: 'value3', b: 'value4' }
 *  }
 * @param items The array of records
 * @param key The key we want to use to associate the array items
 */
export const associateByKey = <T extends Record<string, any>>(
    items: T[],
    key: KeysOfStringValue<T>
) => associateBy(items, (item: T) => String(item[key]));

/**
 * A more generic version of `associateByKey`, which can associate by a
 * nested key.
 */
export const associateBy = <T extends object>(
    items: T[],
    keyFunction: (item: T) => string
) => {
    return items.reduce((result, item) => {
        const key = keyFunction(item);
        result[key] = item;

        return result;
    }, {} as Record<string, T>);
};

/**
 * Generate an array of a given length.
 */
export const getCustomLengthArray = (count = 20) => {
    return Array.from<unknown>(Array(count));
};

export type ObjectEntries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

export function objectEntries<T extends Record<string, any>>(
    obj: T
): ObjectEntries<T> {
    return Object.entries(obj) as ObjectEntries<T>;
}

/**
 * Removes duplicate values from an array of strings and sorts them alphabetically.
 * This is commonly used for normalizing arrays of identifiers before sending to APIs.
 *
 * @param arr - The input array of strings that may contain duplicates
 * @returns A new array with only unique values sorted alphabetically
 *
 * @example
 * // Returns ["apple", "banana", "orange"]
 * uniqueSortedStrings(["banana", "apple", "orange", "apple"])
 */
export const uniqueSortedStrings = (arr: string[]) =>
    Array.from(new Set(arr)).sort();
